import { Checkbox, Icon, Box, Text } from '@chakra-ui/react';

import {
  LiaSortAmountDownAltSolid,
  LiaSortAmountUpAltSolid,
} from 'react-icons/lia';

import { useATSelection, useATColumns, useATisActive } from '../hooks';
import type { ATHeaderProps } from '../actionTable/ATTypes';
import { InfoTip } from '../../ui/toggle-tip';

// Custom Sticky Header Component
export const TableHeader = <
  T extends Record<string, any>,
  TExtra extends Record<string, any> = {},
>({
  items,
  sortOrder,
  sortKey,
  toggleSort,
  isSortable,
  isCheckbox,
  rowKey,
  isMultipleSelection,
  isSelectionDisabled,
}: ATHeaderProps<T, TExtra>) => {
  const { selection } = useATSelection();
  const { isActive, setIsActive } = useATisActive();
  const { columns, columnsConfig } = useATColumns();

  const indeterminate = selection.size > 0 && selection.size < items.length;
  const allChecked = selection.size > 0 && selection.size === items.length;

  return (
    <Box
      bg="bg.subtle"
      borderBottom="1px solid"
      borderColor="border.subtle"
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      width="fit-content"
      position="sticky"
      top={0}
      zIndex={1}
    >
      {/* Checkbox Column */}
      {isCheckbox && (
        <Box
          borderRight="1px solid"
          borderColor="border.subtle"
          visibility={isMultipleSelection ? 'visible' : 'hidden'}
          cursor={isSelectionDisabled ? 'not-allowed' : 'pointer'}
          onClick={() => {
            if (!isSelectionDisabled && isMultipleSelection) {
              setIsActive(true);
              if (allChecked) {
                selection.clearImmediate();
              } else {
                selection.set([...items], rowKey);
              }
            }
          }}
          _hover={{
            bg:
              isSelectionDisabled || !isMultipleSelection
                ? 'transparent'
                : 'bg.hover',
          }}
          p={3}
        >
          <Checkbox.Root
            size="sm"
            colorPalette="blue"
            disabled={isSelectionDisabled || !isMultipleSelection}
            checked={
              indeterminate
                ? ('indeterminate' as 'indeterminate')
                : (allChecked as boolean)
            }
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
          </Checkbox.Root>
        </Box>
      )}

      {/* Data Columns */}
      {columns.map((colKey, colIdx) => {
        return (
          <Box
            key={String(colKey)}
            borderRight="1px solid"
            borderColor="border.subtle"
            cursor={
              isSortable && columnsConfig[colKey].isSortable !== false
                ? 'pointer'
                : 'default'
            }
            color="tertiary.fg"
            onClick={() => {
              if (isSortable && columnsConfig[colKey].isSortable !== false) {
                setIsActive(true);
                toggleSort(colKey);
              }
            }}
            _hover={{
              bg:
                isSortable && columnsConfig[colKey].isSortable !== false
                  ? 'bg.emphasized'
                  : 'transparent',
            }}
            _last={{
              borderRight: 'none',
            }}
            p={
              columnsConfig[colKey].width === '0px' ||
              columnsConfig[colKey].width === '0'
                ? '0px'
                : '3'
            }
          >
            <Text
              width={columnsConfig[colKey].width ?? '120px'}
              textStyle="table.header"
              textWrap="wrap"
              textAlign="center"
              gap="4"
              overflow="hidden"
            >
              {columnsConfig[colKey].label || colKey || ''}
              {columnsConfig[colKey].infoTip && (
                <InfoTip stopPropagation={true}>
                  {columnsConfig[colKey].infoTip}
                </InfoTip>
              )}
              {isSortable && columnsConfig[colKey].isSortable !== false && (
                <Icon
                  visibility={isActive ? 'visible' : 'hidden'}
                  as={
                    sortOrder === 'asc' && sortKey === colKey
                      ? LiaSortAmountUpAltSolid
                      : LiaSortAmountDownAltSolid
                  }
                  boxSize="16px"
                  flexShrink={0}
                />
              )}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};
